import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Calendar, Location, ArrowUp2, ArrowDown2 } from "iconsax-react";
import cn from "classnames";
import { EmptyState } from "@/shared/components";
import { FileViewer } from "@/shared/ui";
import { formatDateForDisplay } from "@/shared/utils";
import type { ExperienceRecord } from "../../types/personalSheet";
import ProfileDataRow from "../ProfileDataRow";

interface Props {
  records: ExperienceRecord[];
  hasNoExperience?: boolean;
  workProofUrl?: string | null;
  enpfUrl?: string | null;
}

export default function ExperienceSection({ records, hasNoExperience, workProofUrl, enpfUrl }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  if (records.length === 0) {
    if (hasNoExperience) {
      return <p className="text-body-regular-md content-base-secondary">{t("personalSheet.noExperienceDeclared")}</p>;
    }
    return <EmptyState noDataText={t("personalSheet.empty.experience")} noDataHint={t("personalSheet.empty.experienceHint")} compact />;
  }

  function toggleExpand(id: number) {
    setExpandedIds(function updateSet(prev) {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {records.map(function renderRecord(record) {
        const isExpanded = expandedIds.has(record.id);
        const ArrowIcon = isExpanded ? ArrowUp2 : ArrowDown2;

        return (
          <div key={record.id} className="border surface-base-stroke radius-md overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(record.id)}
              className={cn("w-full px-4 py-3 flex items-center justify-between cursor-pointer", isExpanded && "pb-4 border-b surface-base-stroke")}>
              <span className="text-body-bold-md content-base-primary flex-1 min-w-0 text-left truncate">
                {record.organization_name || record.work_position}
              </span>
              <ArrowIcon size={16} color="currentColor" className="shrink-0 ml-3" />
            </button>
            {isExpanded && (
              <div className="px-4 pt-4 pb-4">
                <div className="flex flex-col">
                  <ProfileDataRow icon={Briefcase} label={t("personalSheet.fields.organizationName")} value={record.organization_name} />
                  <ProfileDataRow icon={Briefcase} label={t("personalSheet.fields.specialty")} value={record.speciality} />
                  <ProfileDataRow icon={Briefcase} label={t("personalSheet.fields.workPosition")} value={record.work_position} />
                  <ProfileDataRow icon={Location} label={t("personalSheet.fields.workAddress")} value={record.work_address || null} />
                  <ProfileDataRow icon={Briefcase} label={t("personalSheet.fields.workFormat")} value={record.work_format ? t(`personalSheet.workFormatValues.${record.work_format}`) : null} />
                  <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.beginningDate")} value={formatDateForDisplay(record.beginning_date)} />
                  <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.endDate")} value={record.is_current ? t("personalSheet.fields.currentlyWorking") : formatDateForDisplay(record.end_date)} />
                </div>
              </div>
            )}
          </div>
        );
      })}
      {workProofUrl && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.workProofFileLabel")}</span>
          <FileViewer existingFileUrl={workProofUrl} isDownloadable />
        </div>
      )}
      {enpfUrl && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.enpfFileLabel")}</span>
          <FileViewer existingFileUrl={enpfUrl} isDownloadable />
        </div>
      )}
    </div>
  );
}
